import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, CheckCircle, XCircle, Loader2, Calendar, 
  FileText, AlertTriangle, ExternalLink, Search, Building,
  ShieldCheck, ShieldX, Award
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = '';

const VerifyReportPage = () => {
  const [searchParams] = useSearchParams();
  const [reportId, setReportId] = useState(searchParams.get('id') || '');
  const [loading, setLoading] = useState(false);
  const [verification, setVerification] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    // Auto-verify if ID is in URL
    const idFromUrl = searchParams.get('id');
    if (idFromUrl) {
      setReportId(idFromUrl);
      verifyReport(idFromUrl);
    }
  }, [searchParams]);

  const verifyReport = async (id) => {
    if (!id || !id.trim()) {
      return;
    }

    setLoading(true);
    setSearched(true);
    setVerification(null);

    try {
      const response = await fetch(`${API_URL}/api/verify-report?id=${encodeURIComponent(id.trim())}`);
      
      if (!response.ok) {
        throw new Error('Verification failed');
      }

      const data = await response.json();
      setVerification(data);
    } catch (err) {
      console.error('Verification error:', err);
      setVerification({
        verified: false,
        message: 'Unable to verify report. Please check the Report ID and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    verifyReport(reportId);
  };

  const getVerdictColor = (verdict) => {
    if (!verdict) return 'text-gray-400';
    const v = verdict.toUpperCase();
    // RED verdicts
    if (v.includes('HIGH') || v.includes('CRITICAL') || v.includes('DANGEROUS') || v.includes('SCAM') || v === 'RED') return 'text-red-400';
    // AMBER verdicts
    if (v.includes('SUSPICIOUS') || v.includes('MEDIUM') || v.includes('CAUTION') || v === 'AMBER') return 'text-amber-400';
    // GREEN verdicts (SAFE, GREEN, LOW, LOW RISK)
    return 'text-green-400';
  };

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16" data-testid="verify-report-page">
      <Helmet>
        <title>Verify Report | ActionFraudOne</title>
        <meta name="description" content="Verify the authenticity of an ActionFraudOne fraud intelligence report." />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-[#C3A86B]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#C3A86B]/30">
              <Shield className="w-10 h-10 text-[#C3A86B]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Report Verification</h1>
            <p className="text-gray-400">
              Verify the authenticity of an ActionFraudOne fraud intelligence report
            </p>
          </div>

          {/* Search Form */}
          <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <Input
                    type="text"
                    placeholder="Enter Report ID (e.g., AFO-20260417-ABC123)"
                    value={reportId}
                    onChange={(e) => setReportId(e.target.value)}
                    className="pl-10 bg-[#0a1628] border-[#1e40af]/30 text-white placeholder:text-gray-500 h-12"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={loading || !reportId.trim()}
                  className="bg-[#C3A86B] hover:bg-[#b39759] text-black font-semibold px-6 h-12"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <Card className="bg-[#0d1d32] border-[#1e40af]/30">
              <CardContent className="p-8 text-center">
                <Loader2 className="w-12 h-12 text-[#C3A86B] animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Verifying report...</p>
              </CardContent>
            </Card>
          )}

          {/* Verification Result */}
          {!loading && searched && verification && (
            <>
              {verification.verified ? (
                <Card className="bg-green-500/10 border-green-500/30">
                  <CardContent className="p-8">
                    {/* Verified Header */}
                    <div className="text-center mb-8">
                      <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-green-500/50">
                        <ShieldCheck className="w-12 h-12 text-green-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-2">Report Verified</h2>
                      <p className="text-green-400 font-medium">{verification.message}</p>
                    </div>

                    {/* Verification Badge */}
                    <div className="flex items-center justify-center gap-2 mb-6">
                      <Award className="w-5 h-5 text-[#C3A86B]" />
                      <span className="text-[#C3A86B] font-semibold">OFFICIAL VERIFIED REPORT</span>
                    </div>

                    {/* Report Details */}
                    <div className="bg-[#0a1628]/50 rounded-xl p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                        <span className="text-gray-400">Report ID</span>
                        <span className="text-[#C3A86B] font-mono font-semibold">{verification.report_id}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                        <span className="text-gray-400">Entity Checked</span>
                        <span className="text-white font-medium">{verification.entity_checked}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                        <span className="text-gray-400">Entity Type</span>
                        <span className="text-white">{verification.entity_type}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                        <span className="text-gray-400">Verdict</span>
                        <span className={`font-bold ${getVerdictColor(verification.verdict)}`}>
                          {verification.verdict}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                        <span className="text-gray-400">Risk Score</span>
                        <span className={`font-bold ${getVerdictColor(verification.verdict)}`}>
                          {verification.risk_score}/100
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                        <span className="text-gray-400">Analysis Type</span>
                        <span className="text-white">{verification.analysis_type}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                        <span className="text-gray-400">Issue Date</span>
                        <span className="text-white flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          {verification.issue_date ? new Date(verification.issue_date).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3">
                        <span className="text-gray-400">Status</span>
                        <span className="text-green-400 flex items-center gap-2 font-semibold">
                          <CheckCircle className="w-5 h-5" />
                          {verification.status}
                        </span>
                      </div>
                    </div>

                    {/* Issuer Info */}
                    <div className="mt-6 p-4 bg-[#0a1628]/50 rounded-xl border border-[#1e40af]/20 space-y-3">
                      <p className="text-gray-300 text-sm leading-relaxed">
                        This report has been generated and verified using the ActionFraudOne Fraud Intelligence System.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        ActionFraudOne operates as an independent fraud intelligence and verification platform, delivering structured risk assessments based on aggregated data signals, OSINT intelligence, and behavioural analysis.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        Each report is uniquely registered and can be authenticated using the Report ID via the official ActionFraudOne verification portal.
                      </p>
                      <p className="text-gray-500 text-xs leading-relaxed">
                        This report is provided for informational purposes only and does not constitute legal advice.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-red-500/50">
                      <ShieldX className="w-10 h-10 text-red-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Report Not Found</h2>
                    <p className="text-gray-400 mb-4">{verification.message}</p>
                    <p className="text-gray-500 text-sm">
                      Report ID searched: <span className="text-white font-mono">{reportId}</span>
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Instructions */}
          {!searched && (
            <Card className="bg-[#0d1d32] border-[#1e40af]/30">
              <CardContent className="p-8">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#C3A86B]" />
                  How to Verify Your Report
                </h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#1e40af]/20 rounded-full flex items-center justify-center text-xs text-[#C3A86B] font-bold flex-shrink-0">1</span>
                    <span>Find your Report ID on your PDF report (top of first page)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#1e40af]/20 rounded-full flex items-center justify-center text-xs text-[#C3A86B] font-bold flex-shrink-0">2</span>
                    <span>Enter the Report ID in the search box above (e.g., AFO-20260417-ABC123)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#1e40af]/20 rounded-full flex items-center justify-center text-xs text-[#C3A86B] font-bold flex-shrink-0">3</span>
                    <span>Click "Verify" to confirm the report's authenticity</span>
                  </li>
                </ol>
                
                <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-400 text-sm">
                      Only reports generated by ActionFraudOne can be verified through this system. 
                      If verification fails, the report may be fraudulent.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer Links */}
          <div className="text-center mt-8">
            <Link to="/check" className="text-[#C3A86B] hover:text-[#b39759] text-sm font-medium">
              Get a Scam Check
            </Link>
            <span className="text-gray-600 mx-3">|</span>
            <Link to="/" className="text-gray-400 hover:text-gray-300 text-sm">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyReportPage;
